<template>
  <div v-clickOutside="closeModal" class="h-full">
    <div class="relative mt-10">
      <span
        v-if="hasUnreadNotification.length > 0"
        class="w-6 h-6 rounded-full absolute top-3 z-100 left-24"
        :class="hasUnreadNotification.length <= 2 ? 'bg-yellow-500' : 'bg-error'"
      />
      <ButtonModule
        prefix="icon-inbox text-21"
        tooltip-text="Inbox"
        is-white-tooltip
        tooltip-position="top-1/2 transform -translate-y-1/2 left-full z-50 ml-25"
        tooltip-classes="text-13 border border-grey-700 px-13 py-9 font-medium rounded-8"
        tooltip-bg-opacity="bg-opacity-80"
        class="relative p-7 rounded-6"
        :class="[btnClasses, isDarkMode ? 'bg-book' : 'hover:bg-grey-400']"
        @click="openModal"
      />
    </div>

    <SidebarModal ref="sidebarModalRef" :width="400">
      <div class="h-full mx-auto pt-20 bg-primary">
        <!-- header -->
        <div class="mb-40 px-15 ">
          <h1 class="text-24 font-bold leading-none">Inbox</h1>
        </div>
        <!-- content -->
        <div class="flex flex-col h-full">
          <div class="flex px-10 md:px-15">
            <Switcher v-model="activeState" :is-dark-mode="isDarkMode" class="max-w-320" @onChange="onChange">
              <template #title0>
                <p class="flex justify-center items-center text-14 md:text-15">About you <span v-if="hasUnreadPersonalMessages" class="w-8 h-8 rounded-full bg-brand ml-10 flex-shrink-0" /></p>
              </template>
              <template #title1>
                <p class="flex justify-center items-center text-14 md:text-15">Stay up-to-date <span v-if="hasUnreadGeneralMessages" class="w-8 h-8 rounded-full bg-brand ml-10 flex-shrink-0" /></p>
              </template>
            </Switcher>
          </div>

          <div v-if="filteredNotifications.length > 0" class="mt-40 pb-100 overflow-y-auto px-15">
            <NotificationItem
              v-for="(notification, index) in filteredNotifications"
              :key="notification.id"
              :notification="notification"
              :has-border="index !== 0"
            />
          </div>

          <div v-else class="text-center w-full max-w-420 rounded-10 py-50 mt-40" :class="isDarkMode ? 'bg-project' : 'bg-grey-450 '">
            <img :src="require(`@/assets/images/inbox.svg`)" class="w-full max-w-200 mx-auto" alt="teams">
            <p class="font-bold text-17 mt-50">Your inbox is empty</p>
            <p class="text-grey-1050">Once there is news, we'll let you know.</p>
          </div>
        </div>
      </div>
    </SidebarModal>
  </div>
</template>

<script>
import Switcher from '@/components/shared/Switcher'

import { defineComponent, computed, ref, onMounted, nextTick, watch } from 'vue'
import { useStore } from 'vuex'

import NotificationItem from '@/components/inbox/NotificationItem'

import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import SidebarModal from '@/components/sidebar/SidebarModal'
import { useRoute } from 'vue-router'

export default defineComponent({
  components: {
    Switcher,
    NotificationItem,
    SidebarModal,
    ButtonModule
  },

  setup () {
    const { state, getters, dispatch } = useStore()
    const route = useRoute()

    const sidebarModalRef = ref(null)

    const activeState = ref(true)

    const settings = computed(() => state.settings.settings)

    const isDarkMode = computed(() => getters.isDarkMode)

    const filteredNotifications = computed(() => {
      const category = activeState.value ? 'personal' : 'general'

      return state.inbox.notifications[category] || []
    })

    const hasUnreadNotification = computed(() => {
      const personal = state.inbox.notifications.personal.filter(item => item.status === 'unread')
      const general = state.inbox.notifications.general.filter(item => item.status === 'unread')
      return [...personal, ...general]
    })

    const btnClasses = computed(() => {
      const prefix = sidebarModalRef.value?.show ? '' : 'hover:'
      const lightModeClasses = sidebarModalRef.value?.show ? 'border border-brand text-brand bg-grey-200 shadow-sidebar' : 'border border-transparent'

      return isDarkMode.value ? `${prefix}bg-project ${prefix}text-white border border-transparent` : lightModeClasses
    })

    const hasUnreadGeneralMessages = computed(() => state.inbox.notifications.general?.some(item => item.status === 'unread'))
    const hasUnreadPersonalMessages = computed(() => state.inbox.notifications.personal?.some(item => item.status === 'unread'))

    watch(() => [route.params, route.query], () => sidebarModalRef.value.close())

    onMounted(() => {
      nextTick(() => {
        const ids = state.inbox.notifications.personal.filter(item => item.status === 'unread').map(item => item.id)

        if (ids.length > 0) dispatch('updateNotificationStatus', { ids })
      })
    })

    function onChange () {
      const ids = filteredNotifications.value.filter(item => item.status === 'unread').map(item => item.id)
      if (ids.length > 0) {
        setTimeout(() => dispatch('updateNotificationStatus', { ids }), 1000)
      }
    }

    function openModal () {
      sidebarModalRef.value.open()
    }

    function closeModal () {
      sidebarModalRef.value.close()
    }

    return {
      // refs
      sidebarModalRef,
      // data
      activeState,
      // computed
      settings,
      btnClasses,
      isDarkMode,
      filteredNotifications,
      hasUnreadNotification,
      hasUnreadGeneralMessages,
      hasUnreadPersonalMessages,
      // functions
      onChange,
      openModal,
      closeModal
    }
  }
})
</script>
